import { Auth } from "shared/@auth/Auth";
import { useMutation, useQuery, useQueryClient } from "react-query";
import createNotification from "shared/@utils/createNotification";

export function useIssues() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  const {
    data: transactionIssues,
    isLoading: isTransactionIssuesLoading,
    isRefetching: isTransactionIssuesRefetching,
    refetch: refetchTransactionIssues,
  } = useQuery(["transactionIssues"], () =>
    makeAuthedRequest(`v1/events/transaction-issues`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json().catch(() => ({}));
        throw new Error(getErrorMessage(body));
      }
    })
  );

  const {
    data: ticketIssues,
    isLoading: isTicketIssuesLoading,
    isRefetching: isTicketIssuesRefetching,
    refetch: refetchTicketIssues,
  } = useQuery(["ticketIssues"], () =>
    makeAuthedRequest(`v1/events/ticket-issues`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json().catch(() => ({}));
        throw new Error(getErrorMessage(body));
      }
    })
  );

  const resolveTransactionIssue = useMutation(
    async ({ transactionId, paymentId, actionCode, iboNumber }) => {
      const res = await makeAuthedRequest(`v1/events/transaction-issues`, {
        method: "POST",
        body: JSON.stringify({
          transactionId,
          paymentId,
          actionCode,
          iboNumber,
        }),
      });

      const body = await res.json().catch(() => []);

      if (!res.ok) {
        throw new Error(getErrorMessage(body));
      }

      return body;
    },
    {
      onSuccess: (data) => {
        const success = Number(data?.[0]?.Success) === 1;

        if (success) {
          createNotification({
            type: "success",
            title: "Success",
            message: "Issue successfully cleared",
            autoDismiss: true,
          });
          queryClient.refetchQueries(["transactionIssues"]);
        } else {
          createNotification({
            type: "danger",
            title: "Failed",
            message: "Issue was not successfully cleared",
            autoDismiss: true,
          });
        }
      },
      onError: (error) => {
        createNotification({
          type: "danger",
          title: "Failed",
          message: error?.message || "Issue was not successfully cleared",
          autoDismiss: true,
        });
      },
    }
  );

  const resolveTicketIssue = useMutation(
    async ({ attendeeTicketId, actionCode, iboNumber }) => {
      const res = await makeAuthedRequest(`v1/events/ticket-issues`, {
        method: "POST",
        body: JSON.stringify({
          attendeeTicketId,
          actionCode,
          iboNumber,
        }),
      });

      const body = await res.json().catch(() => []);

      if (!res.ok) {
        throw new Error(getErrorMessage(body));
      }

      return body;
    },
    {
      onSuccess: (data) => {
        const success = Number(data?.[0]?.Success) === 1;

        if (success) {
          createNotification({
            type: "success",
            title: "Success",
            message: "Issue successfully cleared",
            autoDismiss: true,
          });
          queryClient.refetchQueries(["ticketIssues"]);
        } else {
          createNotification({
            type: "danger",
            title: "Failed",
            message: "Issue was not successfully cleared",
            autoDismiss: true,
          });
        }
      },
      onError: (error) => {
        createNotification({
          type: "danger",
          title: "Failed",
          message: error?.message || "Issue was not successfully cleared",
          autoDismiss: true,
        });
      },
    }
  );

  return {
    transactionIssues,
    ticketIssues,

    refetchTransactionIssues,
    refetchTicketIssues,

    isTransactionIssuesLoading,
    isTicketIssuesLoading,
    isTransactionIssuesRefetching,
    isTicketIssuesRefetching,

    resolveTransactionIssue,
    resolveTicketIssue,
  };
}

const getErrorMessage = (body) => {
  const possibleMessages = [
    body?.error?.message?.message?.responsetext,
    body?.error?.message?.message?.responseText,
    body?.error?.message?.message?.message,

    body?.error?.message?.responsetext,
    body?.error?.message?.responseText,
    body?.error?.message?.message,

    body?.error?.responsetext,
    body?.error?.responseText,
    body?.error?.message,

    body?.message?.message?.responsetext,
    body?.message?.message?.responseText,
    body?.message?.message?.message,

    body?.message?.responsetext,
    body?.message?.responseText,
    body?.message?.message,

    typeof body?.error === "string" ? body.error : null,
    typeof body?.message === "string" ? body.message : null,
  ];

  return (
    possibleMessages.find(
      (value) => typeof value === "string" && value.trim()
    ) || "Unknown error"
  );
};
